/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionOpenSignaturePad';

const ActionOpenSignaturePadManager: ActionManagerFn<
  'ActionOpenSignaturePad'
> = (platform: Platform): ActionManager<'ActionOpenSignaturePad'> => {
  return {
    ...baseActionManager(platform, 'ActionOpenSignaturePad'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Signature Pad ItemID',
        type: 'ItemID',
        initialFilter: 'SignaturePad',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'SignaturePad') {
            return `Item id '${value}' doesn't belong to a Signature Pad`;
          }
          return '';
        },
      },
    },
    name: 'Open Signature Pad',
    getDisplayText: (item) => item.props.targetItemId,
    icon: { iconName: 'file-signature' },
    description: `**Overview**<br/>
The Open Signature Pad action is a specialized UI interaction component designed to activate a "Signature Pad" component. This target component is typically a canvas that allows users to draw their signature using a mouse or touch input. This action functions as the trigger to reveal or activate this capture interface, often used in approval workflows or document verification steps.<br/><br/>
This action is tightly coupled with the SignaturePad component type. It ensures that the target ID provided references a valid signature component, preventing configuration errors. By programmatically triggering the pad, developers can integrate signature capture seamlessly into button clicks or workflow transitions.

**Key Features**<br/>
* **Targeted Activation:** Specifically designed to trigger the display or activation logic of a Signature Pad component.
* **Strict Type Validation:** Implements validateItemId to ensure the selected targetItemId is explicitly a SignaturePad. If a different component type is selected, the builder returns an error.
* **Registry Filtering:** The property picker uses initialFilter: 'SignaturePad', streamlining the configuration process by restricting the dropdown list to show only valid signature components available on the page.
* **Visual Context:** The editor displays the targetItemId directly in the action list (getDisplayText), making it clear which signature element is being triggered.

**Properties**<br/>
* **Signature Pad ItemID (targetItemId):** A property of type ItemID.
  * *Function:* Identifies the unique ID of the Signature Pad component to be opened.
  * *Validation:* Must reference a valid component of type 'SignaturePad'.

**Use Cases**<br/>
* **Delivery Confirmation:** A "Sign for Package" button on a courier's app that opens the signature pad for the recipient to acknowledge receipt.
* **Contract Approval:** A "Finalize Agreement" button that triggers the signature pad for the user to sign a digital contract.
* **Field Service:** Allowing a technician to capture a customer's signature after completing a repair job by tapping a "Complete Job" action.`,
  };
};

export default ActionOpenSignaturePadManager;
